// Cruise Command · Federated Authentication Client Library (RC3 · 2026-07-23)
// -----------------------------------------------------------------------
// Wraps the two provider-specific JavaScript SDKs (Sign in with Apple JS
// and MSAL for Microsoft Entra ID) behind a single normalised interface:
//
//     const nonce = generateNonce();
//     const { id_token } = await signInWithApple({ nonce });
//     const { id_token } = await signInWithMicrosoft({ nonce });
//
// Everything provider-specific — SDK loading, popup flow, error mapping
// — is confined to this module so `Login.jsx` can stay purely UX.
//
// The tokens returned here are OIDC id_tokens (JWTs signed by the IdP).
// They are then handed to the open-registration backend endpoint:
//     • POST /api/auth/sso/{provider}/signin
//       (handles both first-time signup and returning sign-in — see
//        routes_sso_signin.py)
//
// No credentials are minted client-side. The backend verifies every
// token against the provider's public JWKS.
//
// Provider prerequisites (out of scope for this file, tracked in the
// RC3 Authentication Validation Report addendum):
//   Apple      — Services ID `REACT_APP_APPLE_CLIENT_ID` must have the
//                current origin registered as a Web Domain + Return URL
//                in the Apple Developer Portal, AND the domain-
//                association file must be served at
//                /.well-known/apple-developer-domain-association.txt.
//   Microsoft  — App Registration `REACT_APP_MICROSOFT_CLIENT_ID` must
//                list `window.location.origin` as a Single-page
//                application redirect URI in Azure Entra ID.
//
// When those platform configs are missing the SDKs will surface an
// authentic IdP error (invalid_client / AADSTS50011) which we bubble
// up unmodified — see `describeFederatedAuthError()`.

import { PublicClientApplication, EventType } from "@azure/msal-browser";

// -----------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------

/**
 * Cryptographically random nonce (base64url · ~256 bits of entropy).
 * The nonce is required by Apple and strongly recommended for Microsoft
 * — the backend cross-checks it against the id_token's `nonce` claim.
 */
export function generateNonce() {
  const bytes = new Uint8Array(32);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return _base64UrlEncode(bytes);
}

/**
 * Trigger Sign in with Apple. Resolves with `{ id_token, code?, user? }`.
 *
 * `nonce` must match the value posted alongside the id_token to the
 * backend — the backend's `AppleProvider` will fail token validation
 * if they diverge.
 *
 * @throws {FederatedAuthError} with code === "apple_popup_closed" (web) |
 *   "apple_not_completed" (native 1001) | "apple_configuration_missing" |
 *   "apple_platform_error" | "apple_unknown"
 */
export async function signInWithApple({ nonce }) {
  const { isNativePlatform } = await import("@/lib/platform");
  if (isNativePlatform()) return _signInWithAppleNative({ nonce });
  const clientId = process.env.REACT_APP_APPLE_CLIENT_ID;
  if (!clientId) {
    throw new FederatedAuthError("apple_configuration_missing",
      "Apple sign-in is not configured on this build.");
  }
  await _loadAppleSdk();
  try {
    // Apple JS SDK expects a hashed `nonce` sent to `AppleID.auth.init`
    // — the returned id_token embeds the SHA-256 hash which our backend
    // compares against the plaintext nonce we send with the id_token.
    // We therefore pass the plaintext nonce to Apple; Apple hashes it.
    window.AppleID.auth.init({
      clientId,
      scope: "name email",
      redirectURI: window.location.origin + "/",
      state: nonce,          // Apple echoes state back for CSRF binding
      nonce,                 // Apple hashes this into the id_token
      usePopup: true,
    });
    const response = await window.AppleID.auth.signIn();
    // response shape:
    //   { authorization: { code, id_token, state }, user? }
    const auth = response?.authorization || {};
    if (!auth.id_token) {
      throw new FederatedAuthError("apple_unknown",
        "Apple sign-in did not return an id_token.");
    }
    return {
      id_token: auth.id_token,
      code: auth.code || null,
      user: response?.user || null,
    };
  } catch (err) {
    if (err instanceof FederatedAuthError) throw err;
    // Apple returns errors on `err.error` — e.g. "popup_closed_by_user",
    // "invalid_client", "invalid_request". Surface the raw code so the
    // Validation Report addendum can point Platform Ops at the exact
    // Apple Developer Portal setting that's missing.
    const code = err?.error || "";
    if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") {
      throw new FederatedAuthError("apple_popup_closed",
        "Apple sign-in was cancelled.");
    }
    if (code === "invalid_client" || code === "invalid_request") {
      throw new FederatedAuthError("apple_platform_error",
        `Apple sign-in refused this domain (${code}). The Services ID ` +
        `must have this origin registered as a Web Domain + Return URL ` +
        `and the domain-association file served at ` +
        `/.well-known/apple-developer-domain-association.txt.`,
        { providerErrorCode: code });
    }
    throw new FederatedAuthError("apple_unknown",
      `Apple sign-in failed${code ? ` (${code})` : ""}.`,
      { providerErrorCode: code });
  }
}

/**
 * NATIVE PREPARATION (not yet built/tested on a device). Capacitor
 * WKWebView blocks the Apple JS popup, so native shells use
 * ASAuthorizationController via `@capacitor-community/apple-sign-in`.
 * The resulting id_token carries `aud = <bundle id>`; the backend
 * accepts it only when `APPLE_NATIVE_BUNDLE_ID` is configured.
 */
async function _signInWithAppleNative({ nonce }) {
  // iOS identity: com.mycruisecommand.mobile (Team FB8DTX4JU9 · ASC 6816283495).
  // Android's applicationId (com.cruisecommand.mobile) is irrelevant here.
  const iosBundleId = process.env.REACT_APP_IOS_BUNDLE_ID;
  if (!iosBundleId) {
    throw new FederatedAuthError("apple_configuration_missing",
      "Apple sign-in is not configured on this native build.");
  }
  let plugin;
  try {
    ({ SignInWithApple: plugin } = await import("@capacitor-community/apple-sign-in"));
  } catch (_e) {
    throw new FederatedAuthError("apple_configuration_missing",
      "Apple sign-in is not available in this app build.");
  }
  // Stage 1 · ASAuthorizationController. Only plugin rejections are mapped
  // to the neutral "did not complete (<domain> <code>)" message.
  let response;
  try {
    ({ response } = await plugin.authorize({
      clientId: iosBundleId,
      redirectURI: "cruisecommand://auth-callback",
      scopes: "email name",
      nonce,
    }));
  } catch (err) {
    throw mapNativeAppleError(err);
  }
  if (!response?.identityToken) {
    throw new FederatedAuthError("apple_unknown", "Apple sign-in did not return an id_token.");
  }
  const name = [response.givenName, response.familyName].filter(Boolean).join(" ");

  // Stage 2 · mobile session. The native session is the bearer pair minted by
  // POST /api/auth/mobile/apple/exchange and persisted in secure storage.
  // The web endpoint (/api/auth/sso/apple/signin) only sets a cookie, which
  // the Capacitor shell cannot present on later requests. Backend errors
  // (401 / 409 account_exists_unlinked / 5xx) propagate as HTTP errors for
  // describeSsoBackendError; a storage/malformed-response failure is
  // reported as its own code so it is never mistaken for an Apple rejection.
  const { handleAppleMobileHandoff } = await import("@/lib/mobile/mobileAuthSession");
  try {
    await handleAppleMobileHandoff({ id_token: response.identityToken, nonce, display_name: name || null });
  } catch (err) {
    if (err?.response || err?.isAxiosError) throw err;
    throw new FederatedAuthError("apple_session_not_established",
      "Apple verified your identity, but the app could not save the session on this device. Please try again.",
      { cause: _trim(String(err?.message || err), 120) });
  }
  return {
    native: true,
    id_token: response.identityToken,
    code: response.authorizationCode || null,
    user: name ? { name: { firstName: response.givenName, lastName: response.familyName } } : null,
  };
}

/** Plugin rejection → FederatedAuthError (exported for regression tests). */
export function mapNativeAppleError(err) {
  const msg = String(err?.message || err?.code || "");
  const chain = formatNativeAppleErrorChain(err?.data);
  const detail = chain || _trim(msg, 120);
  const meta = { providerErrorCode: detail, nativeErrorChain: err?.data?.chain || null };
  // Every native rejection is reported neutrally with its domain/code and
  // any underlying chain. ASAuthorizationError 1001 ("canceled") is raised
  // both for a user dismissal and for system-side aborts, so it is NOT
  // labelled as a user action; the chain, when present, is evidence only.
  if (/1001|cancel/i.test(msg)) {
    return new FederatedAuthError("apple_not_completed",
      `Apple sign-in did not complete${detail ? ` (${detail})` : ""}.`, meta);
  }
  return new FederatedAuthError("apple_platform_error",
    `Apple sign-in did not complete${detail ? ` (${detail})` : ""}.`, meta);
}

/** Native plugin `reject(..., data)` → "ASAuthorizationError 1000 ← AKAuthenticationError -7026 "…"" (or ""). */
export function formatNativeAppleErrorChain(data) {
  const chain = Array.isArray(data?.chain) ? data.chain : [];
  return chain
    .filter((e) => e && e.domain != null && e.code != null)
    .map((e, i) => {
      const domain = String(e.domain).replace(/^com\.apple\.AuthenticationServices\./, "");
      const desc = i > 0 && e.description ? ` "${_trim(String(e.description), 80)}"` : "";
      return `${domain} ${e.code}${desc}`;
    })
    .join(" ← ");
}

/** Native: exchange succeeded and tokens were stored, but /api/auth/me did not hydrate. */
export function nativeSessionNotEstablishedError(provider) {
  const label = _providerLabel(provider) || "your identity provider";
  return {
    code: "native_session_not_established",
    title: `Signed in with ${label}, but the session could not be established`,
    message: "Your identity was verified, but the app could not load your account. Please try again.",
  };
}

/** Apple's first-authorization `user.name` → "First Last" (or null). */
export function appleDisplayName(user) {
  const n = user?.name;
  if (!n) return null;
  const full = [n.firstName, n.lastName].filter(Boolean).join(" ").trim();
  return full || null;
}

// -----------------------------------------------------------------------
// Microsoft Entra · loginRedirect() flow (AUTH-MS-003 · Track 1 · 2026-02-20)
// -----------------------------------------------------------------------
// Popup mode was retired after RCA showed that Microsoft's login
// pages ship `Cross-Origin-Opener-Policy: same-origin`. That header
// severs `window.opener` for the lifetime of the popup, which breaks
// both MSAL's own `monitorPopupForHash()` polling AND every relay
// heuristic that keys off the opener reference. The redirect flow
// avoids the popup altogether: the current tab navigates to Entra,
// Entra sends the browser back to `redirectUri` (= `window.location.
// origin`), and MSAL processes the fragment via
// `handleRedirectPromise()` in the *same* tab — no cross-window,
// no COOP interaction.
//
// The nonce is stashed in sessionStorage so it survives the redirect
// and is available to `consumeMicrosoftRedirect()` on the return
// leg. (The retired invitation-token stash was removed 2026-02-20
// when the access-by-invitation gate was retired in favour of open
// registration — see routes_sso_signin.py.)
//
// Public contract:
//   • initiateMicrosoftRedirect({ nonce })
//       → resolves on `loginRedirect()` (returns undefined; navigation
//         happens next tick).
//   • consumeMicrosoftRedirect()
//       → called from the callback route on app boot. Returns
//         `{ id_token, account, nonce }` when a response was
//         processed, or `null` if there is nothing to do. Throws
//         `FederatedAuthError` on IdP failure.

const _MS_PENDING_KEY = "cc_msal_pending";

/**
 * Begin Microsoft Entra sign-in via full-page redirect.
 *
 * The current tab navigates to Entra. The nonce is stashed in
 * sessionStorage so it survives the redirect and is available to
 * `consumeMicrosoftRedirect()` on the return leg.
 *
 * @throws {FederatedAuthError} with code === "microsoft_configuration_missing"
 *   when the client ID is absent — no navigation occurs in that case.
 */
export async function initiateMicrosoftRedirect({ nonce, intent = "signin" } = {}) {
  const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
  if (!clientId) {
    throw new FederatedAuthError("microsoft_configuration_missing",
      "Microsoft sign-in is not configured on this build.");
  }
  // Native shell: MSAL's redirect_uri would be `capacitor://localhost`
  // (AADSTS50011). Run the public-client PKCE flow in the system browser.
  // No sessionStorage pending record on native: the WebView stays alive while
  // the system browser is up, so a cancelled attempt must not make
  // hasPendingMicrosoftRedirect() hijack the next render (or a Google return).
  // The return leg is routed purely by the deep-link hash (`code=`/`error=`);
  // state/verifier live single-use in Preferences (survives cold start).
  const { isNativePlatform } = await import("@/lib/platform");
  if (isNativePlatform()) {
    const native = await import("@/lib/mobile/nativeMicrosoftAuth");
    await native.startNativeMicrosoftSignIn({ nonce, intent });
    return;
  }
  try {
    sessionStorage.setItem(_MS_PENDING_KEY, JSON.stringify({
      nonce,
      intent,                 // "signin" | "link" (connect to the signed-in account)
      startedAt: Date.now(),
    }));
  } catch (_e) {
    // Private-mode / storage disabled — sign-in still works; the
    // nonce is echoed via the id_token so the backend can verify it.
  }
  const msal = await _getMsalInstance();
  try {
    // loginRedirect resolves with `undefined` — the browser navigates
    // away before the next tick. Any error MSAL surfaces here is a
    // pre-navigation config error (e.g. missing clientId).
    await msal.loginRedirect({
      scopes: ["openid", "email", "profile"],
      prompt: "select_account",
      nonce,
    });
  } catch (err) {
    _clearMicrosoftPending();
    throw _mapMsalRedirectError(err);
  }
}

/**
 * Complete a Microsoft Entra sign-in that was started via
 * `initiateMicrosoftRedirect()`. Safe to call on every app mount —
 * short-circuits with `null` when there is no pending record and no
 * MSAL response fragment in the URL.
 *
 * Callers are responsible for posting the returned `id_token` and
 * `nonce` to `POST /api/auth/sso/microsoft/signin` — this module
 * only owns the IdP-side handshake.
 *
 * @throws {FederatedAuthError} on IdP failure.
 */
export async function consumeMicrosoftRedirect() {
  if (typeof window === "undefined") return null;
  const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
  if (!clientId) return null;

  let pending = null;
  try {
    const raw = sessionStorage.getItem(_MS_PENDING_KEY);
    if (raw) pending = JSON.parse(raw);
  } catch (_e) { /* ignore malformed / disabled */ }

  const hash = (window.location.hash || "");
  const hasFragment = hash.includes("code=") || hash.includes("error=");
  if (!pending && !hasFragment) return null;

  // Native return leg: `cruisecommand://auth-callback?code=…&state=…` was
  // merged into the hash by NativeBridge. MSAL never ran here.
  const { isNativePlatform } = await import("@/lib/platform");
  if (isNativePlatform()) {
    _clearMicrosoftPending();
    if (!hasFragment) return null; // nothing to consume (cancelled / stale)
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const native = await import("@/lib/mobile/nativeMicrosoftAuth");
    return native.consumeNativeMicrosoftReturn(params);
  }

  const msal = await _getMsalInstance();
  let result;
  try {
    result = await msal.handleRedirectPromise();
  } catch (err) {
    _clearMicrosoftPending();
    throw _mapMsalRedirectError(err);
  }

  if (!result || !result.idToken) {
    // No token — either nothing was in the URL, or MSAL swallowed a
    // silent renewal. Drop stale pending records so a fresh attempt
    // is not confused with an in-flight one.
    if (pending) _clearMicrosoftPending();
    return null;
  }

  const nonce = pending?.nonce || null;
  const intent = pending?.intent === "link" ? "link" : "signin";
  _clearMicrosoftPending();
  return {
    id_token: result.idToken,
    account: result.account || null,
    nonce,
    intent,
  };
}

function _clearMicrosoftPending() {
  try { sessionStorage.removeItem(_MS_PENDING_KEY); } catch (_e) { /* ignore */ }
}

function _mapMsalRedirectError(err) {
  if (err instanceof FederatedAuthError) return err;
  const raw = err?.errorCode || err?.name || "";
  const message = err?.errorMessage || err?.message || "";
  // AADSTS50011 → redirect URI not registered on the App Registration.
  // AADSTS900971 → no reply address provided (SPA platform missing).
  if (/AADSTS500(11|97|89|58)/.test(message) || /redirect_uri/i.test(message)) {
    return new FederatedAuthError("microsoft_platform_error",
      `Microsoft Entra refused this origin. The App Registration must ` +
      `have "${window.location.origin}" listed as a Single-page ` +
      `application redirect URI. Raw error: ${_trim(message, 200)}`,
      { providerErrorCode: raw, providerErrorMessage: message });
  }
  return new FederatedAuthError("microsoft_unknown",
    `Microsoft sign-in failed${raw ? ` (${raw})` : ""}. ${_trim(message, 160)}`,
    { providerErrorCode: raw, providerErrorMessage: message });
}

/** True when a Microsoft redirect is currently in-flight — used by
 *  the AuthGate to gate rendering on the callback processor.  */
export function hasPendingMicrosoftRedirect() {
  if (typeof window === "undefined") return false;
  try {
    if (sessionStorage.getItem(_MS_PENDING_KEY)) return true;
  } catch (_e) { /* ignore */ }
  const hash = (window.location.hash || "");
  return hash.includes("code=") || hash.includes("error=");
}

/**
 * Normalise an axios/network error from an SSO sign-in HTTP call into
 * a human-safe display object.
 */
export function describeSsoBackendError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const errorCode = (detail && typeof detail === "object") ? detail.error : null;
  if (status === 401) {
    return {
      code: "identity_verification_failed",
      title: "Verification failed",
      message: "Your identity provider could not be verified. Please try again.",
    };
  }
  if (status === 503 && errorCode === "sso_provider_unavailable") {
    return {
      code: "sso_provider_unavailable",
      title: "Provider temporarily unavailable",
      message: "This identity provider is not currently registered on the " +
               "server. Please try another provider or contact your administrator.",
    };
  }
  if (status === 409 && errorCode === "account_exists_unlinked") {
    const methods = (detail.sign_in_methods || []).map(_providerLabel).filter(Boolean);
    const via = methods.length ? methods.join(" or ") : "your existing method";
    return {
      code: "account_exists_unlinked",
      title: "This email already has a Cruise Command account",
      message: `Sign in with ${via}, then connect ${_providerLabel(detail.provider)} ` +
               "under Settings → Sign-in methods. We never merge accounts on an email match alone.",
    };
  }
  if (status === 409 && errorCode === "identity_linked_elsewhere") {
    return {
      code: "identity_linked_elsewhere",
      title: "Already connected to another account",
      message: `That ${_providerLabel(detail.provider)} identity is connected to a different Cruise Command account.`,
    };
  }
  if (status === 409 && errorCode === "last_sign_in_method") {
    return {
      code: "last_sign_in_method",
      title: "Keep at least one sign-in method",
      message: "Connect another sign-in method before disconnecting this one.",
    };
  }
  return {
    code: "unknown",
    title: "Sign-in failed",
    message: (typeof detail === "string" && detail) ||
             (detail && detail.message) ||
             err?.message ||
             "Something went wrong signing you in.",
  };
}

const PROVIDER_LABELS = { google: "Google", apple: "Apple", microsoft: "Microsoft" };
function _providerLabel(p) { return PROVIDER_LABELS[String(p || "").toLowerCase()] || null; }
/** "Apple" | "Google" | "Microsoft" | null — for "Signed in with …" copy. */
export function providerLabel(p) { return _providerLabel(p); }

/** Apple "Hide My Email" relay address (…@privaterelay.appleid.com): mail is forwarded by Apple. */
export function isApplePrivateRelayEmail(email) {
  return /@privaterelay\.appleid\.com$/i.test(String(email || "").trim());
}
export const APPLE_PRIVATE_RELAY_NOTE =
  "This is an Apple Hide My Email address. Cruise Command mail is forwarded by Apple to your real inbox; replies and your identity stay private.";

/**
 * Custom error class carrying the machine-readable code alongside the
 * user-visible message. Consumers should switch on `error.code`.
 */
export class FederatedAuthError extends Error {
  constructor(code, message, meta = null) {
    super(message);
    this.name = "FederatedAuthError";
    this.code = code;
    this.meta = meta || {};
  }
}

// -----------------------------------------------------------------------
// Apple SDK loader — memoised, single injection
// -----------------------------------------------------------------------
const APPLE_SDK_URL =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
let _appleSdkPromise = null;

function _loadAppleSdk() {
  if (typeof window.AppleID?.auth?.signIn === "function") {
    return Promise.resolve();
  }
  if (_appleSdkPromise) return _appleSdkPromise;
  _appleSdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${APPLE_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(
        new FederatedAuthError("apple_configuration_missing",
          "Failed to load the Apple sign-in SDK.")));
      return;
    }
    const script = document.createElement("script");
    script.src = APPLE_SDK_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      _appleSdkPromise = null;
      reject(new FederatedAuthError("apple_configuration_missing",
        "Failed to load the Apple sign-in SDK. Check your network."));
    };
    document.head.appendChild(script);
  });
  return _appleSdkPromise;
}

// -----------------------------------------------------------------------
// AUTH-MS-003 · Track 1 · The former `handleMsalPopupCallbackIfPresent()`
// popup relay was retired 2026-02-20. Microsoft now uses full-page
// redirect (see `initiateMicrosoftRedirect` / `consumeMicrosoftRedirect`
// above). The popup-vs-parent COOP severing that broke the previous
// implementation no longer applies.
// -----------------------------------------------------------------------

// -----------------------------------------------------------------------
// MSAL PublicClientApplication — memoised singleton
// -----------------------------------------------------------------------
let _msalInstance = null;
let _msalInitPromise = null;

async function _getMsalInstance() {
  if (_msalInstance) return _msalInstance;
  if (_msalInitPromise) return _msalInitPromise;
  _msalInitPromise = (async () => {
    const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
    const tenant = process.env.REACT_APP_MICROSOFT_TENANT || "common";
    if (!clientId) {
      throw new FederatedAuthError("microsoft_configuration_missing",
        "Microsoft sign-in is not configured on this build.");
    }
    const instance = new PublicClientApplication({
      auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenant}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
      },
      cache: {
        // sessionStorage → wiped when the tab closes; safer for a
        // shared-device travel context than localStorage.
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false,
      },
    });
    await instance.initialize();
    // Silence "no active account" warnings across renders.
    instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
        instance.setActiveAccount(event.payload.account);
      }
    });
    _msalInstance = instance;
    return instance;
  })();
  return _msalInitPromise;
}

// -----------------------------------------------------------------------
// Small utilities
// -----------------------------------------------------------------------
function _base64UrlEncode(bytes) {
  let str = "";
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function _trim(text, max) {
  if (!text) return "";
  return text.length <= max ? text : text.slice(0, max) + "…";
}
